import * as Cesium from "cesium";
import { DEFAULT_ORBIT_COLOR } from "../../../utils/constants";
import { ContactStation, GroundStation } from "../types";
import { offline } from "../../../utils/commonFunctions";
import { createMultiGroundObjectPacket, getEntitysByType } from "../../../utils/cesiumUtil";
import { calculateOrbitPeriodSeconds } from "../../CommonComponents/OrbitPeriods";

type SatelliteData = {
  lat: number;
  lon: number;
  alt: number;
};

let listeners: ((data: SatelliteData) => void)[] = [];

export function onSatelliteUpdate(callback: (data: SatelliteData) => void) {
  listeners.push(callback);

  // Return an unsubscribe function (so React cleanup works)
  return () => {
    listeners = listeners.filter((cb) => cb !== callback);
  };
}

export function emitSatelliteUpdate(data: SatelliteData) {
  listeners.forEach((cb) => cb(data));
}

type Interval = {
  "boolean": boolean,
  "interval": string
}


function remove_entity(viewer: Cesium.Viewer, entityId: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    try {
      if (viewer.entities.getById(entityId)) {
        viewer.entities.removeById(entityId);
      }
      resolve(); // Resolve the promise after removal (or if no entity existed)
    } catch (error) {
      reject(error); // Reject the promise if an error occurs
    }
  });
}

export const getSatellitePackete = (document: any, satellites: any, selectedSatId?: string, dashboard?: string) => {
  const satelliteData: any[] = [];
  const czmlPacket: any[] = [];

  const description = {
    "description": "Ground Access Metadata Packet",
    "id": "e63330c5-2283-49df-8a23-fa26d92f6ac8",
    "name": "Ground Access"
  }
  satellites.length && satellites?.forEach(satellite => {

    if (satellite?.availability && satellite?.satOrbitPoint?.length) {
      // If dashboard is Operate, the leadtime and trailTime are set to different values for better visualization of orbit
      const leadTimeNumber = dashboard && (dashboard === "operate" || dashboard === 'TRUETWIN_COMMAND_CENTER') ? 5400 : 5000;
      const trailTimeNumber = 5000;

      const satellitePackete = {
        "availability": satellite?.availability,
        "billboard": {
          "eyeOffset": {
            "cartesian": [
              0,
              0,
              0
            ]
          },
          "horizontalOrigin": "CENTER",
          "image": require('./cesiumImages/selectedSatellite.png'),
          "pixelOffset": {
            "cartesian2": [
              0,
              0
            ]
          },
          "scale": 0.75,
          "show": true,
          "verticalOrigin": "CENTER"
        },
        "description": "Attached Object to satellite",
        "id": satellite?.satId,
        "name": satellite?.satName,
        "path": {
          "leadTime": [
            {
              "number": leadTimeNumber
            }
          ],
          "width": satellite?.simulatedOrbit ? 2 : 0.5,
          "material": {
            "solidColor": {
              "color": {
                "rgba": satellite?.satId === selectedSatId ? [204, 245, 78, 255] : [138, 138, 138, 250]
              }
            }
          },
          "show": [
            {
              "boolean": true,
              "interval": satellite?.availability
            }
          ],
          "trailTime": [
            {
              "number": trailTimeNumber
            }
          ]
        },
        "position": {
          "cartographicDegrees": satellite?.satOrbitPoint,
          "epoch": satellite?.availability?.split('/')[0]
        }
      }
      satelliteData.push(satellitePackete)
    }
  })

  if (satelliteData.length > 0) {
    czmlPacket.push(document, description, ...satelliteData);
  }
  return czmlPacket;
}

export const createSatellitePackets = async (
  viewer: Cesium.Viewer,
  groupsSatellites: any[], // Assuming VisualizationDataType is equivalent to any[]
  selectedSatId?: string,
  dashboard?: string,
  modelInfo?: string
): Promise<string> => {
  return new Promise<string>(async (resolve, reject) => {
    try {
      if (!groupsSatellites?.length) {
        reject('No satellite groups provided.');
        return;
      }
      const promises = groupsSatellites.flatMap((groupSatellites: any) => {
        if (!groupSatellites?.satellites?.length) return [];

        return groupSatellites.satellites.map(async (satellite: any) => {
          if (satellite?.availability && satellite?.satOrbitPoint?.length) {
            const isSimulate = dashboard !== "operate";
            const rgbaArray = (satellite?.color !== '' && !satellite?.color.includes('#')) ? JSON.parse(satellite?.color) : DEFAULT_ORBIT_COLOR;
            const satelliteName = satellite?.satName?.length > 15 ? satellite?.satName?.substring(0, 15) : satellite?.satName
            rgbaArray[3] = 150;
            const leadTimeNumber = dashboard && (dashboard === "operate" || dashboard === 'TRUETWIN_COMMAND_CENTER') ? 5400 : calculateOrbitPeriodSeconds(satellite?.satOrbitParameters?.altitude);
            const trailTimeNumber = 5000;
            const satellitePacket = {
              availability: satellite?.availability,
              ...(modelInfo && {
                model: {
                  gltf: modelInfo,
                  scale: 0.1,
                  minimumPixelSize: 50
                }
              }),
              ...(!modelInfo && {
                billboard: {
                  eyeOffset: { cartesian: [0, 0, 0] },
                  horizontalOrigin: "CENTER",
                  image: require('./cesiumImages/selectedSatellite.png'),
                  pixelOffset: { cartesian2: [0, 0] },
                  scale: 0.75,
                  show: true,
                  verticalOrigin: "CENTER"
                }
              }),
              description: 'orbit',
              id: satellite?.satId,
              name: satellite?.satName,
              ...(isSimulate && {
                path: {
                  leadTime: [{ number: leadTimeNumber }],
                  trailTime: [{ number: trailTimeNumber }],
                  width: satellite?.simulatedOrbit ? 2 : 0.5,
                  material: {
                    solidColor: {
                      color: {
                        rgba:
                          satellite?.satId === selectedSatId
                            ? [204, 245, 78, 255]
                            : rgbaArray ?? [138, 138, 138, 250],
                      },
                    },
                  },
                },
              }),
              "label": {
                "text": satellite?.satId === selectedSatId ? satelliteName : '',
                "font": "12px Montserrat",
                "fillColor": { "rgba": [204, 245, 78, 255] },
                "outlineColor": { "rgba": [0, 0, 0, 255] },
                "outlineWidth": 2,
                "style": "FILL_AND_OUTLINE",
                "horizontalOrigin": "LEFT",
                "verticalOrigin": "CENTER",
                "pixelOffset": { "cartesian2": [12, 0] },
                "showBackground": true,
                "backgroundColor": { "rgba": [0, 0, 0, 200] },       // semi‑opaque black
                "backgroundPadding": { "cartesian2": [4, 4] }         // px padding around the text
              },
              position: {
                cartographicDegrees: satellite?.satOrbitPoint,
                epoch: satellite?.availability?.split('/')[0],
              },
            };

            // Build path packets
            const buildPathPacket = (
              suffix: string,
              pathProps: any
            ) => {
              // Ensure width is always > 0 to avoid Cesium DeveloperError
              const normalizedWidth = (pathProps && typeof pathProps.width === 'number' && pathProps.width > 0)
                ? pathProps.width
                : 1;

              const normalizedProps = {
                ...pathProps,
                width: normalizedWidth,
              };

              return {
                availability: satellite?.availability,
                id: `${satellite?.satId}_${suffix}`,
                position: {
                  cartographicDegrees: satellite?.satOrbitPoint,
                  epoch: satellite?.availability?.split('/')[0],
                },
                path: {
                  // default show; can be overridden if pathProps includes show
                  show: [{ boolean: true }],
                  ...normalizedProps,
                },
              };
            };

            // normalized color chooser for path materials
            const baseRgba = satellite?.satId === selectedSatId
              ? [204, 245, 78]
              : (rgbaArray ? rgbaArray.slice(0, 3) : [138, 138, 138]);

            // Helper to create rgba with configurable alpha
            const rgbaWithAlpha = (alpha: number) => [...baseRgba, alpha];

            // Full trail: shows the entire orbit trail
            const satelliteTrailPathPacket = buildPathPacket('fullTrail', {
              trailTime: [{ number: 0 }, { number: trailTimeNumber }],
              leadTime: [{ number: 0 }],
              width: 1,
              material: {
                solidColor: {
                  color: { rgba: rgbaWithAlpha(satellite?.satId === selectedSatId ? 90 : (rgbaArray ? (rgbaArray[3] ?? 250) : 250)) },
                },
              },
            });

            // Glow trail near the satellite for emphasis (use width 2 as before)
            const glowTrailPacket = buildPathPacket('glowTrail', {
              trailTime: [{ number: 0 }, { number: 300 }],
              leadTime: [{ number: 0 }],
              width: 2,
              material: {
                polylineGlow: {
                  color: { rgba: rgbaWithAlpha(satellite?.satId === selectedSatId ? 255 : (rgbaArray ? (rgbaArray[3] ?? 90) : 90)) },
                  glowPower: 0.5,
                  taperPower: 1.0,
                },
              },
            });

            // Lead path: dashed line ahead of the satellite
            const satelliteLeadPathPacket = buildPathPacket('lead', {
              leadTime: [{ number: leadTimeNumber }],
              trailTime: [{ number: 0 }],
              width: 1,
              material: {
                polylineDash: {
                  color: { rgba: rgbaWithAlpha(90) },
                  dashLength: 10,
                },
              },
            });

            // Glow lead: short dashed lead segment with higher alpha
            const glowLeadPacket = buildPathPacket('glowLead', {
              leadTime: [{ number: 0 }, { number: 300 }],
              trailTime: [{ number: 0 }],
              width: 2,
              material: {
                polylineDash: {
                  color: { rgba: rgbaWithAlpha(satellite?.satId === selectedSatId ? 255 : 255) },
                  dashLength: 10,
                },
              },
            });

            const documentPacket = {
              ...(groupSatellites?.documentMetadata ?? {}),
              name: `Satellite-${satellite?.satId}`,
            };

            const czmlPacket = [
              documentPacket,
              satellitePacket,

              ...(dashboard === "operate"
                ? [
                  satelliteTrailPathPacket,
                  glowTrailPacket,
                  satelliteLeadPathPacket,
                  glowLeadPacket,
                ]
                : []),
            ];
            if (selectedSatId === satellite?.satId) {
              const satellites = groupSatellites?.['satellites'] ? groupSatellites?.['satellites'] : [];
              const aois = groupSatellites?.['areaOfInterest'] ? groupSatellites?.['areaOfInterest'] : [];
              const groundTargets = groupSatellites?.['groundTarget'] ? groupSatellites?.['groundTarget'] : [];
              const groundStations = groupSatellites?.['groundStation'] ? groupSatellites?.['groundStation'] : [];
              const satToGsConnection = groupSatellites?.['satToGsConnection'] ? groupSatellites?.['satToGsConnection'] : [];
              const satToGTConnection = groupSatellites?.['satToGTConnection'] ? groupSatellites?.['satToGTConnection'] : [];
              const { stations, taskplans, faultplans, multientity } = getEntitysByType(groundStations || [], groundTargets || [], []);
              const stationIds = stations.map((station) => station.uniqueId);
              const featureIds = taskplans.map((feature) => feature.uniqueId);
              const allFeatures = groundTargets.filter((feature: any) => featureIds.includes(feature?.groundTargetId));
              const groundStationczml = getGroundStationPacket(satellites, groundStations, false, multientity);
              const aoiCzml = getAoiPacket(satellites, aois);
              const groundTargetCzml = getGroundTargetPacket(satellites, allFeatures);
              createMultiGroundObjectPacket(viewer, multientity)
              const satToGTConnections = satelliteToGroundTargetConnection(satToGTConnection);
              const satToGsConnections = groundStationToSatelliteConnection(satToGsConnection);
              czmlPacket.push(...groundStationczml, ...aoiCzml, ...groundTargetCzml, ...satToGsConnections, ...satToGTConnections);
              removeAllGsAnimatedRings(viewer);
              if ((dashboard === "operate" || dashboard === 'TRUETWIN_COMMAND_CENTER') && activeContactGsIds.size > 0) {
                addGsAnimatedRings(viewer, groundStations);
              }
            }
            removeDataSourceByName(viewer, `Satellite-${satellite?.satId}`);
            const dataSource = await Cesium.CzmlDataSource.load(czmlPacket);
            await viewer.dataSources.add(dataSource);
            setOrbitAnimationDetails(viewer, dataSource, selectedSatId, dashboard);
          }
        });
      });

      await Promise.all(promises.flat());
      resolve('All satellites plotted successfully.');

    } catch (error) {
      console.error('Error plotting satellites:', error);
      reject(error);
    }
  });
};

const setOrbitAnimationDetails = (viewer, dataSource, satelliteId, dashboard?: string) => {
  if (!viewer || !dataSource || !dataSource.entities) return;

  let entity = dataSource.entities.getById(satelliteId);

  // In operate dashboard, some CZML packets may not use the same ID
  // as the URL `sat_id`. Fallback to the first entity with a position
  // so that we still emit live coordinates.
  if (!entity && (dashboard === "operate" || dashboard === 'TRUETWIN_COMMAND_CENTER')) {
    const entitiesArray = dataSource.entities.values || [];
    entity =
      entitiesArray.find((e: any) => e && e.position) ||
      entitiesArray[0];
  }

  if (!entity) return;
  entity.label = new Cesium.LabelGraphics({
    text: new Cesium.CallbackProperty(() => {
      const currentTime = viewer.clock.currentTime;
      const position = entity.position?.getValue(currentTime);
      if (!position) return "";
      const cartographic = Cesium.Ellipsoid.WGS84.cartesianToCartographic(position);
      const lat = Cesium.Math.toDegrees(cartographic.latitude).toFixed(2);
      const lon = Cesium.Math.toDegrees(cartographic.longitude).toFixed(2);
      const alt = cartographic.height.toFixed(2);
      emitSatelliteUpdate({
        lat: Number(lat) || 0,
        lon: Number(lon) || 0,
        alt: Number(alt) / 1000 || 0,
      });
    }, false),
  });
}

export const getOrbitPacket = (satellites: any[]) => {

  const satelliteData: any[] = [];
  const czmlPacket: any[] = [];

  satellites?.forEach(satellite => {
    if (satellite?.availability && satellite?.satOrbitPoint?.length) {
      const satellitePackete = {
        "availability": satellite?.availability,
        "description": "Attached extart orbit to satellite orbit",
        "id": satellite?.satId,
        "name": satellite?.satName,
        "path": {
          "width": 2,
          "material": {
            "polylineGlow": {
              "color": {
                "rgba": [240, 240, 240, 240],
              },
              "glowPower": 0.2,
              "taperPower": 0.5,
            },
          },
          "show": [
            {
              "boolean": true,
              "interval": satellite?.availability
            }
          ],
        },
        "position": {
          "cartographicDegrees": satellite?.satOrbitPoint,
          "epoch": satellite?.availability?.split('/')[0]
        }
      }
      satelliteData.push(satellitePackete)
    }
  })


  if (satelliteData.length > 0) {
    czmlPacket.push(
      {
        "clock": {
          interval: satellites[0]?.availability,
          "multiplier": 1,
          "range": "CLAMPED",
          "step": "SYSTEM_CLOCK_MULTIPLIER"
        },
        "id": "document",
        "name": "Orbit_Object",
        "version": "1.0"
      },
      ...satelliteData);
  }
  return czmlPacket;
}


export const getGroundStationPacket = (satellites: any[], groundStations: any[], isGsAccess: boolean, multientity?: any[]) => {
  let multiEntityArray: string[] = [];
  multientity?.forEach(entity => {
    if (Array.isArray(entity?.Ids)) {
      multiEntityArray.push(...entity.Ids);
    }
  });
  const gsData: any[] = [];
  if (satellites?.length > 0 && groundStations?.length > 0) {
    groundStations?.forEach(station => {
      const isGsPresentAlready = multiEntityArray.includes(station?.gsId);
      if (station?.gsPosition?.length > 2) {
        const stationPackete = {
          "availability": satellites[0]?.availability,
          "billboard": isGsPresentAlready ? '' : {
            "eyeOffset": {
              "cartesian": [
                0,
                0,
                0
              ]
            },
            "horizontalOrigin": "CENTER",
            "image": '/assets/select_gs.svg',
            "pixelOffset": {
              "cartesian2": [
                0,
                0
              ]
            },
            "scale": 0.8,
            "show": true,
            "verticalOrigin": "CENTER"
          },
          "description": "station",
          "id": station?.gsId,
          "label": {
            "fillColor": {
              "rgba": [
                255,
                255,
                255,
                255
              ]
            },
            "font": "montserrat",
            "horizontalOrigin": "LEFT",
            "outlineColor": {
              "rgba": [
                0,
                0,
                0,
                255
              ]
            },
            "outlineWidth": 2,
            "pixelOffset": {
              "cartesian2": [
                12,
                0
              ]
            },
            "show": true,
            "style": "FILL_AND_OUTLINE",
            "verticalOrigin": "CENTER"
          },
          "position": {
            "cartographicDegrees": [
              station?.gsPosition?.[0],
              station?.gsPosition?.[1],
              station?.gsPosition?.[2]
            ]
          },
          "ellipse": isGsAccess ? {
            "semiMinorAxis": 900000, // Adjust the value based on the desired size
            "semiMajorAxis": 900000, // Adjust the value based on the desired size
            "material": {
              "solidColor": {
                "color": {
                  "rgba": [
                    204,
                    245,
                    78,
                    0.40
                  ]
                }
              }
            },
            "height": 0,
            "extrudedHeight": 0,
            "rotation": 0,
            "stRotation": 0,
            "fill": true,
          } : ''
        }
        gsData.push(stationPackete);
      }
    })
  }
  return gsData;
}

export const getAoiPacket = (satellites: any[], aois: any[]) => {
  const aoiData: any[] = [];
  if (satellites?.length > 0 && aois?.length > 0) {
    aois?.forEach((aoi, index) => {
      const aoiPacket = {
        "availability": satellites[0].availability,
        "description": "aoi",
        "id": "Facility/aoi/" + index,
        "name": (aoi?.name ? aoi?.name : aoi?.aoiName),
        "polygon": {
          "extrudedHeight": 0,
          "height": 0,
          "material": {
            "solidColor": {
              "color": {
                "rgba": offline() ? [191, 191, 191, 100] :
                  aoi?.color ? JSON.parse(aoi?.color) : [204, 245, 78, 20]
              }
            }
          },
          "outline": true,
          "outlineColor": {
            "rgba": [204, 244, 94, 255]
          },
          "outlineWidth": 2,
          "arcType": "RHUMB",
          "positions": {
            "cartographicDegrees": aoi.polygon ? getCartographicDegrees(aoi?.polygon) : aoi?.aoiPosition
          }
        }
      }
      aoiData.push(aoiPacket)
    })
  }
  return aoiData;
}

export const getGroundTargetPacket = (satellites: any[], groundTargets: any[]) => {
  const groundTargetData: any[] = [];
  if (satellites?.length > 0 && groundTargets?.length > 0) {
    groundTargets?.forEach((groundTarget, index) => {
      const gtPacket = {
        "availability": satellites[0]?.availability,
        "billboard": {
          "eyeOffset": {
            "cartesian": [
              0,
              0,
              0
            ]
          },
          "horizontalOrigin": "CENTER",
          "image": groundTarget?.color ? groundTarget?.color === "[242, 169, 50, 100]" ? '/assets/point_green.svg' : require('./cesiumImages/gsTarget.png') : require('./cesiumImages/gsTarget.png'),
          "pixelOffset": {
            "cartesian2": [
              0,
              0
            ]
          },
          "scale": 1,
          "show": true,
          "verticalOrigin": "CENTER"
        },
        "description": "target",
        "id": groundTarget?.groundTargetId,
        "label": {
          "fillColor": {
            "rgba": [
              255,
              255,
              255,
              255
            ]
          },
          "font": "montserrat",
          "horizontalOrigin": "LEFT",
          "outlineColor": {
            "rgba": [
              0,
              0,
              0,
              255
            ]
          },
          "outlineWidth": 2,
          "pixelOffset": {
            "cartesian2": [
              12,
              0
            ]
          },
          "show": true,
          "style": "FILL_AND_OUTLINE",
          "verticalOrigin": "CENTER"
        },
        "name": "0dcfed5e-7868-4470-9fb3-068da73a5c7a",
        "position": {
          "cartographicDegrees": (groundTarget?.coordinates?.long) ?
            [
              groundTarget?.coordinates?.long,
              groundTarget?.coordinates?.lat,
              0
            ] : [
              groundTarget?.position?.[0],
              groundTarget?.position?.[1],
              groundTarget?.position?.[2]
            ]
        }
      }
      groundTargetData.push(gtPacket)
    })
  }
  return groundTargetData;
}

//Ground Station to Satellite Connection
export const groundStationToSatelliteConnection = (satToGSConnection: any[]) => {
  const gsToSatConnection: any[] = [];

  satToGSConnection?.forEach(gsToSat => {
    if (!gsToSat?.availability?.length) return

    const interval: Interval[] = [];
    gsToSat?.availability?.forEach((item, key) => {
      const intervalPacket = {
        "boolean": true,
        "interval": item
      }
      interval?.push(intervalPacket)
    })

    const gsToSatPackete = {
      "availability": gsToSat?.availability,
      "description": "Ground Station to Satellite Connection",
      "id": gsToSat?.id,
      "name": gsToSat?.name,
      "parent": gsToSat?.satelliteId,
      "polyline": {
        "arcType": "NONE",
        "material": {
          "solidColor": {
            "color": {
              "rgba": [
                0,
                255,
                255,
                255
              ]
            }
          }
        },
        "positions": {
          "references": [
            gsToSat?.groundStationId + "#position",
            gsToSat?.satelliteId + "#position"
          ]
        },
        "show": interval,
        "width": 1
      }
    }
    gsToSatConnection.push(gsToSatPackete)
  })

  return gsToSatConnection
}

//Ground Station to Satellite Connection
export const satelliteToGroundTargetConnection = (satToGroundTarget: any[]) => {
  const gsToGtConnection: any[] = [];

  satToGroundTarget?.forEach((gtToSat, index) => {
    if (!gtToSat?.availability?.length) return

    const interval: Interval[] = [];
    gtToSat?.availability?.forEach((item, key) => {
      const intervalPacket = {
        "boolean": true,
        "interval": item
      }
      interval.push(intervalPacket)
    })

    const gsToSatPackete = {
      "availability": gtToSat?.availability,
      "description": "Ground Target to Satellite Connection",
      "id": gtToSat?.id + "/" + index,
      "name": gtToSat?.name,
      "parent": gtToSat?.satelliteId,
      "polyline": {
        "arcType": "NONE",
        "material": {
          "solidColor": {
            "color": {
              "rgba": [
                0,
                255,
                255,
                255
              ]
            }
          }
        },
        "positions": {
          "references": [
            gtToSat?.groundTargetId + "#position",
            gtToSat?.satelliteId + "#position"
          ]
        },
        "show": interval,
        "width": 1
      }
    }
    gsToGtConnection.push(gsToSatPackete)
  })

  return gsToGtConnection
}

const getCartographicDegrees = (position) => {
  const cartographicDegrees: any[] = [];
  position.forEach((element: any) => {
    cartographicDegrees.push(element[0]);
    cartographicDegrees.push(element[1]);
    cartographicDegrees.push(0);
  });
  return cartographicDegrees;
}

export function removeDataSourceByName(viewer: any, dataSourceName: string) {
  const dataSources = viewer?.dataSources;
  if (!dataSources) return
  for (let i = 0; i < dataSources.length; i++) {
    const dataSource = dataSources.get(i);
    if (dataSource.name === dataSourceName) {
      dataSources.remove(dataSource);
      return dataSource;
    }
  }
}

export function getDataSourceByName(viewer: any, dataSourceName: string) {
  const dataSources = viewer?.dataSources;
  if (!dataSources) return
  for (let i = 0; i < dataSources.length; i++) {
    const dataSource = dataSources.get(i);
    if (dataSource.name === dataSourceName) {
      return dataSource;
    }
  }
}

export const update_sat_orbit_path = (viewer: Cesium.Viewer | null, current_time: number) => {
  const data_source = getDataSourceByName(viewer, 'Orbit_Object')
  if (data_source) {
    data_source.entities.values.forEach(entity => {
      if (entity) {
        const start_time = Cesium.JulianDate.fromDate(new Date(current_time - 10 * 60000));
        const end_time = Cesium.JulianDate.fromDate(new Date(current_time + 2000));
        const availability = new Cesium.TimeIntervalCollection([
          new Cesium.TimeInterval({
            start: start_time,
            stop: end_time
          })
        ]);
        entity.availability = availability;
      }
    });
  }
}

// --- Ground Station Animation ---
const GS_RING_MAX_RADIUS = 900000;
const GS_RING_DURATION = 3.0; // seconds per pulse cycle

const GS_BIDIRECTIONAL_COLOR = '#3a9f9cff';
const GS_UNIDIRECTIONAL_COLOR = '#9f6e23ff';

// Module-level stores shared between create_gs_packet (hover) and updateGsContactRings (contact updates)
const gsImagePropsStore = new Map<string, Cesium.ConstantProperty>();
const activeContactGsIds = new Set<string>();
// Per-GS connection type derived from uplink/downlink config
const gsConnectionTypeStore = new Map<string, 'bidirectional' | 'unidirectional'>();
// Per-GS position for hover billboard placement
const gsPositionStore = new Map<string, [number, number]>();
// GS enabled/disabled status — false means disabled
let gsStatusListStore: Record<string, boolean> = {};

const isGsDisabled = (gsId: string): boolean => gsStatusListStore[gsId] === false;

const addGsHoverBillboard = (viewer: Cesium.Viewer, gsId: string) => {
  const pos = gsPositionStore.get(gsId);
  if (!pos) return;
  const [lon, lat] = pos;
  const id = `${gsId}_hover`;
  const existing = viewer.entities.getById(id);
  if (existing) viewer.entities.remove(existing);
  viewer.entities.add({
    id,
    position: Cesium.Cartesian3.fromDegrees(lon, lat, 0),
    billboard: {
      image: '/assets/gs_hover.svg',
      horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
      verticalOrigin: Cesium.VerticalOrigin.CENTER,
      scale: 0.8,
      eyeOffset: new Cesium.Cartesian3(0, 0, -100), // render in front of the base billboard
    },
  });
  viewer.scene.requestRender();
};

const removeGsHoverBillboard = (viewer: Cesium.Viewer, gsId: string) => {
  const e = viewer.entities.getById(`${gsId}_hover`);
  if (e) viewer.entities.remove(e);
  viewer.scene.requestRender();
};

const getGsConnectionImage = (gsId: string): string => {
  const type = gsConnectionTypeStore.get(gsId);
  return type === 'bidirectional'
    ? '/assets/connection_bidirectional.svg' : type === 'unidirectional' ?
    '/assets/connection_unidirectional.svg' : '/assets/gs_enabled.svg';
};

const getGsImage = (gsStatus?: string): string => {
  if (gsStatus === 'disabled') return '/assets/gs_disabled.svg';
  return '/assets/gs_enabled.svg'; // default — treat all non-disabled states as enabled
};

const removeGsRingEntities = (viewer: Cesium.Viewer, gsId: string) => {
  [
    `${gsId}_fill`,
    `${gsId}_inner_ring_0`,
    `${gsId}_inner_ring_1`,
    `${gsId}_inner_ring_2`,
  ].forEach(id => {
    const e = viewer.entities.getById(id);
    if (e) viewer.entities.remove(e);
  });
};

export const removeAllGsAnimatedRings = (viewer: Cesium.Viewer | null) => {
  if (!viewer) return;
  const toRemove = viewer.entities.values.filter(e => {
    const id = e.id?.toString() ?? '';
    return id.endsWith('_fill') || /_inner_ring_\d+$/.test(id);
  });
  toRemove.forEach(e => viewer.entities.remove(e));
};

const addGsRingsForPosition = (
  viewer: Cesium.Viewer,
  gsId: string,
  lon: number,
  lat: number,
  animStartMs: number,
  ringColor: string = GS_BIDIRECTIONAL_COLOR
) => {
  removeGsRingEntities(viewer, gsId);
  const center = Cesium.Cartesian3.fromDegrees(lon, lat, 0);

  // Stacked concentric filled ellipses — each inner one covers the center of the outer,
  // creating visible ring bands. Radii are FIXED so semiMajor === semiMinor always.
  // Animation is on material color alpha only (no radius CallbackProperty = no axis error).
  const RINGS = [
    { radius: GS_RING_MAX_RADIUS,        phase: 0.04, baseAlpha: 0.08, peakAlpha: 0.05, height: 0 },
    { radius: GS_RING_MAX_RADIUS * 0.75, phase: 0.06, baseAlpha: 0.20, peakAlpha: 0.10, height: 100 },
    { radius: GS_RING_MAX_RADIUS * 0.50, phase: 0.08, baseAlpha: 0.40, peakAlpha: 0.20, height: 200 },
  ];


  RINGS.forEach(({ radius, phase, baseAlpha, peakAlpha, height }, i) => {
    const id = i === 0 ? `${gsId}_fill` : `${gsId}_inner_ring_${i - 1}`;
    viewer.entities.add({
      id,
      position: center,
      ellipse: {
        semiMinorAxis: radius,
        semiMajorAxis: radius,
        height,
        granularity: Cesium.Math.toRadians(1),
        material: new Cesium.ColorMaterialProperty(
          new Cesium.CallbackProperty(() => {
            const elapsed = (Date.now() - animStartMs) / 1000;
            const raw = (elapsed / GS_RING_DURATION) + phase;
            const t = raw - Math.floor(raw); // 0..1
            // Sine pulse between baseAlpha and peakAlpha
            const alpha = baseAlpha + (peakAlpha - baseAlpha) * Math.sin(t * Math.PI);
            return Cesium.Color.fromCssColorString(ringColor).withAlpha(alpha);
          }, false)
        ),
        fill: true,
        outline: i === 0, // orange outline only on the outermost ring
        outlineColor: Cesium.Color.fromCssColorString(ringColor),
        outlineWidth: 0.08,
      }
    });
  });
};

// For simulation mode (CZML groundStation format: { gsId, gsPosition: [lon, lat, alt] })
export const addGsAnimatedRings = (viewer: Cesium.Viewer | null, groundStations: any[]) => {
  if (!viewer || !groundStations?.length || activeContactGsIds.size === 0) return;
  const ringColor = GS_BIDIRECTIONAL_COLOR; // simulation path — default to bidirectional color
  const animStartMs = Date.now();
  groundStations.forEach(station => {
    if (!station?.gsPosition || station.gsPosition.length < 3) return;
    const [lon, lat] = station.gsPosition;
    addGsRingsForPosition(viewer, station.gsId, lon, lat, animStartMs, ringColor);
  });
  viewer.scene.requestRender();
};

export const create_gs_packet = (gs_data: GroundStation[], viewer: Cesium.Viewer | null, dashboard: string, gsStatusList: Record<string, boolean> = {}) => {
  if (!viewer) return;

  // Sync status store so hover handler can check disabled state
  gsStatusListStore = gsStatusList;

  // Clear and repopulate the module-level stores on each call
  gsImagePropsStore.clear();
  gsPositionStore.clear();

  gs_data.forEach((gs) => {
    // API may return either `groundStationProperties` (ContactStation) or `properties` (GroundStation)
    const gsProp = gs.groundStationProperties ?? (gs as any).properties;
    if (!gs.groundStationId || !gsProp?.latitude || !gsProp?.longitude || !gsProp?.altitude) {
      console.warn(`Ground station data incomplete for ID: ${gs.groundStationId}. Skipping.`);
      return;
    }
    try {
      const latitude = parseFloat(gsProp.latitude);
      const longitude = parseFloat(gsProp.longitude);
      const altitude = parseFloat(gsProp.altitude) * 1000;

      // Remove existing entity synchronously right before adding to avoid duplicate ID errors
      const existing = viewer.entities.getById(gs.groundStationId);
      if (existing) viewer.entities.remove(existing);

      const disabled = isGsDisabled(gs.groundStationId);
      const defaultImage = (dashboard === 'operate' || dashboard === 'TRUETWIN_COMMAND_CENTER')
        ? (disabled ? '/assets/gs_disabled.svg' : getGsImage(gs.status))
        : '/assets/gs_enabled.svg';
      const imageProp = new Cesium.ConstantProperty(defaultImage);
      gsImagePropsStore.set(gs.groundStationId, imageProp);
      // Only register position for hover if GS is enabled
      if (!disabled) {
        gsPositionStore.set(gs.groundStationId, [longitude, latitude]);
      }

      viewer.entities.add({
        id: gs.groundStationId,
        name: gs.groundStationName,
        description: "station",
        position: Cesium.Cartesian3.fromDegrees(longitude, latitude, altitude),
        billboard: {
          eyeOffset: new Cesium.Cartesian3(0, 0, 0),
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          image: imageProp,
          pixelOffset: new Cesium.Cartesian2(0, 0),
          scale: 0.8,
          show: true,
          verticalOrigin: Cesium.VerticalOrigin.CENTER
        },
        label: {
          fillColor: Cesium.Color.WHITE,
          font: "montserrat",
          horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          pixelOffset: new Cesium.Cartesian2(12, 0),
          show: true,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.CENTER
        },
      });

      viewer.scene.requestRender();

    } catch (error) {
      console.error(`Error creating ground station entity for ID: ${gs.groundStationId}`, error);
    }
  });

  if ((dashboard === "operate" || dashboard === 'TRUETWIN_COMMAND_CENTER')) {
    let hoveredGsId: string | null = null;

    viewer.screenSpaceEventHandler.setInputAction((movement: any) => {
      const picked = viewer.scene.pick(movement.endPosition);
      const rawId: string | null = picked?.id?.id ?? null;
      // Map hover overlay pick back to the original GS id so cursor staying on the hover SVG counts as still hovering
      const pickedId = rawId?.endsWith('_hover')
        ? rawId.replace('_hover', '')
        : rawId;
      const pickedGs = pickedId && gsPositionStore.has(pickedId) ? pickedId : null;

      if (pickedGs && pickedGs !== hoveredGsId) {
        // Entered a new GS
        if (hoveredGsId) removeGsHoverBillboard(viewer, hoveredGsId);
        hoveredGsId = pickedGs;
        if (!activeContactGsIds.has(pickedGs)) {
          addGsHoverBillboard(viewer, pickedGs);
        }
      } else if (!pickedGs && hoveredGsId) {
        // Left GS entirely
        removeGsHoverBillboard(viewer, hoveredGsId);
        hoveredGsId = null;
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  }
};

// Updates only billboard images when GS enabled/disabled status changes.
// Does NOT recreate entities — just calls setValue() on the existing ConstantProperty.
export const updateGsStatusImages = (
  viewer: Cesium.Viewer | null,
  gsStatusList: Record<string, boolean>
) => {
  if (!viewer) return;
  gsStatusListStore = gsStatusList;

  gsImagePropsStore.forEach((prop, gsId) => {
    if (isGsDisabled(gsId)) {
      prop.setValue('/assets/gs_disabled.svg');
      // Remove from position store so hover no longer triggers
      gsPositionStore.delete(gsId);
      // Remove any lingering hover billboard
      const hoverEntity = viewer.entities.getById(`${gsId}_hover`);
      if (hoverEntity) viewer.entities.remove(hoverEntity);
      // Remove rings
      removeGsRingEntities(viewer, gsId);
    } else {
      // Restore to connection SVG if in active contact, else default enabled SVG
      prop.setValue(
        activeContactGsIds.has(gsId)
          ? getGsConnectionImage(gsId)
          : getGsImage()
      );
      // Re-register position for hover
      const entity = viewer.entities.getById(gsId);
      if (entity) {
        const pos = entity.position?.getValue(Cesium.JulianDate.now());
        if (pos) {
          const carto = Cesium.Cartographic.fromCartesian(pos);
          gsPositionStore.set(gsId, [
            Cesium.Math.toDegrees(carto.longitude),
            Cesium.Math.toDegrees(carto.latitude),
          ]);
        }
      }
    }
  });

  viewer.scene.requestRender();
};

// Updates ring animations based on which GS are in active contact.
// Call this whenever ongoing_contacts changes — does NOT recreate billboard entities.
export const updateGsContactRings = (
  viewer: Cesium.Viewer | null,
  contacts: ContactStation[],
  gsData: GroundStation[],
  dashboard: string
) => {
  if (!viewer) return;

  // Rebuild active contact and connection-type stores — skip disabled GS
  activeContactGsIds.clear();
  gsConnectionTypeStore.clear();
  (contacts ?? []).forEach(c => {
    if (isGsDisabled(c.groundStationId)) return; // disabled GS never in active contact
    const hasBoth = c.uplinkConfigured && c.downlinkConfigured;
    const hasOne = c.uplinkConfigured || c.downlinkConfigured;
    if (!hasOne) return;
    activeContactGsIds.add(c.groundStationId);
    gsConnectionTypeStore.set(c.groundStationId, hasBoth ? 'bidirectional' : 'unidirectional');
  });

  removeAllGsAnimatedRings(viewer);

  // Update billboard SVGs to reflect contact/disabled state per GS
  gsData.forEach(gs => {
    const prop = gsImagePropsStore.get(gs.groundStationId);
    if (!prop) return;
    if (isGsDisabled(gs.groundStationId)) {
      prop.setValue('/assets/gs_disabled.svg');
    } else {
      prop.setValue(
        activeContactGsIds.has(gs.groundStationId) && (dashboard === 'operate' || dashboard === 'TRUETWIN_COMMAND_CENTER')
          ? getGsConnectionImage(gs.groundStationId)
          : getGsImage(gs.status)
      );
    }
  });

  const animStartMs = Date.now();
  gsData.forEach(gs => {
    if (!activeContactGsIds.has(gs.groundStationId)) return;
    if (!gs.groundStationProperties?.latitude || !gs.groundStationProperties?.longitude) return;
    const type = gsConnectionTypeStore.get(gs.groundStationId);
    const ringColor = type === 'bidirectional' ? GS_BIDIRECTIONAL_COLOR : GS_UNIDIRECTIONAL_COLOR;
    const lat = parseFloat(gs.groundStationProperties.latitude);
    const lon = parseFloat(gs.groundStationProperties.longitude);
    addGsRingsForPosition(viewer, gs.groundStationId, lon, lat, animStartMs, ringColor);
  });

  viewer.scene.requestRender();
};